import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";

import type {
  SupplierDetailResponse,
  SupplierMutationResponse,
  SupplierPayload,
  SupplierFilter,
  SupplierResponse,
} from "../types/suppliers.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.suppliers;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const suppliersService = {
  async list(filter: SupplierFilter): Promise<SupplierResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<SupplierResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load suppliers due to an unexpected error.",
      ) as unknown as SupplierResponse;
    }
  },

  async details(id: number | string): Promise<SupplierDetailResponse> {
    try {
      return await apiClient<SupplierDetailResponse>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(
        error,
        "Failed to load supplier details due to an unexpected error.",
      ) as unknown as SupplierDetailResponse;
    }
  },

  async create(payload: SupplierPayload): Promise<SupplierMutationResponse> {
    try {
      return await apiClient<SupplierMutationResponse>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to create supplier due to an unexpected error.",
      ) as SupplierMutationResponse;
    }
  },

  async update(
    id: number | string,
    payload: Partial<SupplierPayload>,
  ): Promise<SupplierMutationResponse> {
    try {
      return await apiClient<SupplierMutationResponse>(endpoints.update(id), {
        method: "PATCH",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to update supplier due to an unexpected error.",
      ) as SupplierMutationResponse;
    }
  },

  async updateStatus(
    id: number | string,
    payload: { status: "active" | "inactive" },
  ): Promise<SupplierMutationResponse> {
    return suppliersService.update(id, payload);
  },

  async delete(id: number | string): Promise<SupplierMutationResponse> {
    try {
      return await apiClient<SupplierMutationResponse>(endpoints.delete(id), {
        method: "DELETE",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(
        error,
        "Failed to delete supplier due to an unexpected error.",
      ) as SupplierMutationResponse;
    }
  },
};
